<template>
  <div class="sp-picker">
    <div v-if="value" class="sp-picker-chosen">
      <span class="sp-avatar" aria-hidden="true">{{ initials(value.name) }}</span>
      <span class="sp-picker-person">
        <strong>
          {{ value.name }}
          <span v-if="isMe(value)" class="sp-you">you</span>
        </strong>
        <small>{{ details(value) }}</small>
      </span>
      <button type="button" class="btn btn-sm btn-link" @click="clear">Change</button>
    </div>

    <template v-else>
      <div class="sp-picker-search">
        <i class="bi bi-search" aria-hidden="true"></i>
        <input
          ref="input"
          v-model="query"
          type="search"
          class="form-control"
          placeholder="Search by name or email"
          role="combobox"
          aria-autocomplete="list"
          :aria-expanded="results.length ? 'true' : 'false'"
          :aria-controls="listId"
          aria-label="Find the person to assign"
          @input="queueSearch"
          @keydown.down.prevent="move(1)"
          @keydown.up.prevent="move(-1)"
          @keydown.enter.prevent="pickActive"
        />
      </div>
      <button v-if="me && !isCurrent(me)" type="button" class="sp-link-btn sp-picker-self" @click="pick(me)">
        <i class="bi bi-person-check"></i> Assign to me
      </button>
      <ul :id="listId" class="sp-picker-results" role="listbox" aria-label="People">
        <li v-if="loading && !results.length" class="sp-picker-note">
          <span class="spinner-border spinner-border-sm mr-1"></span> Searching…
        </li>
        <li v-else-if="error" class="sp-picker-note">{{ error }}</li>
        <li v-else-if="searched && !results.length" class="sp-picker-note">No one found. Try another name.</li>
        <li
          v-for="(person, index) in results"
          :key="person.id"
          role="option"
          :aria-selected="index === active ? 'true' : 'false'"
          :aria-disabled="isCurrent(person) ? 'true' : null"
          :class="{ active: index === active, current: isCurrent(person) }"
          @mousedown.prevent="pick(person)"
          @mouseenter="active = index"
        >
          <span class="sp-avatar" aria-hidden="true">{{ initials(person.name) }}</span>
          <span class="sp-picker-person">
            <strong>
              {{ person.name }}
              <span v-if="isMe(person)" class="sp-you">you</span>
              <span v-if="person.support_team" class="sp-team">Support team</span>
            </strong>
            <small>{{ details(person) }}</small>
          </span>
          <span v-if="isCurrent(person)" class="sp-picker-tag">Handling it now</span>
        </li>
      </ul>
    </template>
  </div>
</template>

<script>
import { errorMessage } from "../../supportOptions";

let uid = 0;

/**
 * Pick an employee to assign tickets to. Searches as you type; shows how many
 * open tickets each person already has, so work can be spread out.
 * v-model: the chosen person { id, name, email, open_tickets } or null.
 */
export default {
  name: "PersonPicker",
  props: {
    value: { type: Object, default: null },
    // The person the ticket is with now (can't be picked again).
    currentAssigneeId: { type: [Number, String], default: null },
  },
  data() {
    return {
      listId: `sp-picker-${++uid}`,
      query: "",
      results: [],
      active: -1,
      loading: false,
      searched: false,
      error: "",
    };
  },
  computed: {
    me() {
      const user = this.$store.state.user;
      if (!user || !user.id) return null;
      return {
        id: Number(user.id),
        name: `${user.first_name || ""} ${user.last_name || ""}`.replace(/\s+/g, " ").trim(),
        email: user.email || "",
      };
    },
  },
  mounted() {
    this.search();
  },
  beforeDestroy() {
    clearTimeout(this.timer);
  },
  methods: {
    initials(name) {
      return String(name || "?")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join("");
    },
    isMe(person) {
      return !!this.me && Number(person.id) === this.me.id;
    },
    isCurrent(person) {
      return this.currentAssigneeId !== null && Number(person.id) === Number(this.currentAssigneeId);
    },
    details(person) {
      const parts = [person.email];
      if (typeof person.open_tickets === "number") {
        parts.push(person.open_tickets ? `${person.open_tickets} open ${person.open_tickets === 1 ? "ticket" : "tickets"}` : "no open tickets");
      }
      return parts.filter(Boolean).join(" · ");
    },
    queueSearch() {
      clearTimeout(this.timer);
      this.timer = setTimeout(this.search, 250);
    },
    async search() {
      const seq = (this.searchSeq = (this.searchSeq || 0) + 1);
      this.loading = true;
      this.error = "";
      try {
        const { data } = await axios.get("api/support/assignees", { params: { q: this.query.trim() || undefined } });
        if (seq !== this.searchSeq) return;
        this.results = data || [];
        this.active = this.results.findIndex((person) => !this.isCurrent(person));
        this.searched = true;
      } catch (err) {
        if (seq === this.searchSeq) this.error = errorMessage(err, "Couldn't search for people.");
      } finally {
        if (seq === this.searchSeq) this.loading = false;
      }
    },
    move(step) {
      if (!this.results.length) return;
      let next = this.active;
      for (let i = 0; i < this.results.length; i++) {
        next = (next + step + this.results.length) % this.results.length;
        if (!this.isCurrent(this.results[next])) break;
      }
      this.active = next;
    },
    pickActive() {
      if (this.results[this.active]) this.pick(this.results[this.active]);
    },
    pick(person) {
      if (this.isCurrent(person)) return;
      this.$emit("input", person);
    },
    clear() {
      this.$emit("input", null);
      this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
    },
  },
};
</script>
